import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/api";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";

function googleLogin() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/app";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

function AuthShell({ children, title, sub }) {
  const { toggle, lang } = useLang();
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0d3a1e] px-4">
      <div className="grain-overlay relative w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl animate-fade-up">
        <button onClick={toggle} data-testid="auth-lang-toggle" className="absolute right-4 top-4 rounded-full border border-[#165A2E]/30 px-3 py-1 text-xs font-semibold text-[#165A2E] transition-colors duration-200 hover:bg-[#165A2E] hover:text-white">
          {lang === "ms" ? "EN" : "MS"}
        </button>
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#165A2E] font-['Outfit'] text-lg font-bold text-[#84CC16]">G</div>
          <div>
            <h1 className="font-['Outfit'] text-xl font-bold text-[#165A2E]" data-testid="auth-title">{title}</h1>
            <p className="text-sm text-slate-500">{sub}</p>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Login() {
  const { t } = useLang();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/login", form);
      login(r.data.token, r.data.user);
      navigate("/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title={t("welcome")} sub={t("loginSub")}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label>{t("email")}</Label>
          <Input data-testid="login-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" />
        </div>
        <div>
          <Label>{t("password")}</Label>
          <Input data-testid="login-password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" />
        </div>
        <Button data-testid="login-submit-btn" disabled={busy} className="w-full rounded-full bg-[#165A2E] text-white hover:bg-[#114724]">
          {busy ? t("loading") : t("login")}
        </Button>
      </form>
      <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
        <div className="h-px flex-1 bg-slate-200" />{t("or")}<div className="h-px flex-1 bg-slate-200" />
      </div>
      <Button data-testid="google-login-btn" variant="outline" onClick={googleLogin} className="w-full rounded-full">
        <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a7.16 7.16 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/></svg>
        {t("loginGoogle")}
      </Button>
      <p className="mt-6 text-center text-sm text-slate-500">
        {t("noAccount")}{" "}
        <Link to="/register" data-testid="goto-register-link" className="font-semibold text-[#165A2E] hover:underline">{t("register")}</Link>
      </p>
    </AuthShell>
  );
}

export function Register() {
  const { t } = useLang();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", role: "parent" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/register", form);
      if (r.data.pending) {
        toast.success(t("accountPending"));
        navigate("/login");
        return;
      }
      login(r.data.token, r.data.user);
      navigate("/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title={t("register")} sub={t("registerSub")}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label>{t("name")}</Label>
          <Input data-testid="register-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" />
        </div>
        <div>
          <Label>{t("email")}</Label>
          <Input data-testid="register-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" />
        </div>
        <div>
          <Label>{t("phone")}</Label>
          <Input data-testid="register-phone" placeholder="0123456789" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" />
        </div>
        <div>
          <Label>{t("password")}</Label>
          <Input data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" />
        </div>
        <div>
          <Label>{t("registerAs")}</Label>
          <div className="mt-1 grid grid-cols-3 gap-2" data-testid="register-role-group">
            {[["parent", t("asParent")], ["coach", t("asCoach")], ["staff", t("asStaff")]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setForm({ ...form, role: v })} data-testid={`register-role-${v}`}
                className={`rounded-full px-3 py-2 text-xs font-bold transition-colors duration-200 ${form.role === v ? "bg-[#165A2E] text-white" : "border border-[#165A2E]/30 text-[#165A2E] hover:bg-[#165A2E]/5"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
        {form.role !== "parent" && (
          <>
            <div>
              <Label>{t("icNumber")}</Label>
              <Input data-testid="register-ic" required placeholder="900101-14-5678" value={form.ic_number || ""} onChange={(e) => setForm({ ...form, ic_number: e.target.value })} className="mt-1" />
            </div>
            <p className="rounded-lg bg-amber-50 p-3 text-xs font-semibold text-amber-700" data-testid="pending-note">{t("pendingNote")}</p>
          </>
        )}
        <Button data-testid="register-submit-btn" disabled={busy} className="w-full rounded-full bg-[#84CC16] text-[#0A0A0A] hover:bg-[#65A30D]">
          {busy ? t("loading") : t("register")}
        </Button>
      </form>
      <Button data-testid="google-register-btn" variant="outline" onClick={googleLogin} className="mt-4 w-full rounded-full">
        {t("loginGoogle")}
      </Button>
      <p className="mt-6 text-center text-sm text-slate-500">
        {t("haveAccount")}{" "}
        <Link to="/login" data-testid="goto-login-link" className="font-semibold text-[#165A2E] hover:underline">{t("login")}</Link>
      </p>
    </AuthShell>
  );
}
